import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useDynamicReducer } from '@/domains/core/applicationState/hooks/useDynamicReducer';
import { useLazySaga } from '@/domains/core/applicationState/hooks/useLazySaga';
import { getListingReducer } from '@/domains/productDiscovery/Listings/reducer';
import type { SpartacuxProductDiscoveryDomainState } from '@/domains/productDiscovery/productDiscovery.model';
import productRecoReducer from '@/domains/productDiscovery/Recommendation/modules/ProductRecommendation/ProductRecommendation.reducer';

import type { ListingPage } from '../../model';
import {
  requestStateInjection,
  resetInjectionState,
  stateInjected,
} from '../actions';
import type { Injection } from '../model';
import { stateInjectionReducer } from '../reducer';
import { selectInjectionState } from '../selectors/state';

const listingReducer = getListingReducer();

export const useInjectListingState = (
  promisedSaga: () => Promise<() => Generator<unknown, any, unknown>>,
  pageName: ListingPage,
): {
  injectionState: Injection;
  requestState: () => void;
} => {
  useDynamicReducer('productDiscovery.listingInjection', stateInjectionReducer);
  useDynamicReducer('productDiscovery.listing', listingReducer);
  useDynamicReducer(
    'productDiscovery.productRecommendation',
    productRecoReducer,
  );

  const dispatch = useDispatch();
  const [isInjected, setIsInjected] = useState(false);

  const injectionState = useSelector(
    (state: SpartacuxProductDiscoveryDomainState) =>
      selectInjectionState(state, pageName) || 'idle',
  );

  const canInject = ['requested', 'ready'].includes(injectionState);

  const { sagaInjected } = useLazySaga(
    `productDiscovery.listing(${pageName})`,
    promisedSaga,
    {
      allowReInjection: true,
      condition: canInject,
    },
  );

  useEffect(() => {
    if (sagaInjected) {
      setIsInjected(true);
    }
  }, [sagaInjected]);

  useEffect(
    () => () => {
      setIsInjected(false);
      dispatch(resetInjectionState(pageName));
    },
    [dispatch, pageName],
  );

  useEffect(() => {
    if (isInjected) {
      dispatch(stateInjected(pageName));
    }
  }, [isInjected, dispatch, pageName]);

  const requestState = (): void => {
    dispatch(requestStateInjection(pageName));
  };

  return {
    injectionState,
    requestState,
  };
};
